import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DonateWidget } from "./DonateWidget";
import heroFallback from "@/assets/hero-children.webp";

// Resolve seeded `/src/assets/...` paths to bundled URLs so they work in prod.
const assetMap: Record<string, string> = import.meta.glob("/src/assets/*", {
  eager: true,
  import: "default",
});
const resolveAsset = (path?: string | null) =>
  (path && assetMap[path]) || path || heroFallback;

export function Hero() {
  const { data: slides } = useQuery({
    queryKey: ["hero_slides"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hero_slides")
        .select("*")
        .eq("is_published", true)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    retry: 4,
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!slides?.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 7000);
    return () => clearInterval(t);
  }, [slides?.length]);

  const slide = slides?.[idx];

  return (
    <section className="relative overflow-hidden">
      <div className="relative min-h-[640px] w-full md:min-h-[760px]">
        {(slides ?? []).map((s, i) => (
          <img
            key={s.id}
            src={resolveAsset(s.image_url)}
            alt=""
            width={1920}
            height={1080}
            fetchPriority={i === 0 ? "high" : "low"}
            decoding="async"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        {!slides?.length && (
          <img
            src={heroFallback}
            alt=""
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/20 via-foreground/40 to-foreground/80" />

        <div className="container relative mx-auto grid h-full grid-cols-1 items-center gap-10 px-4 py-20 md:grid-cols-2 md:py-28">
          <div className="text-background animate-fade-up">
            {slide?.eyebrow && (
              <span className="inline-block rounded-full bg-background/15 px-3 py-1 text-xs font-medium uppercase tracking-wider backdrop-blur">
                {slide.eyebrow}
              </span>
            )}
            <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.05] text-balance md:text-7xl">
              {slide?.title ?? "Raise Sri Lanka — a 501(c)(3) funding hope for Sri Lankan families"}
            </h1>
            <p className="mt-5 max-w-xl text-lg text-background/85 text-pretty">
              {slide?.subtitle ??
                "Join a global community giving hope, dignity and opportunity to families across Sri Lanka."}
            </p>

            {slides && slides.length > 1 && (
              <div className="mt-8 flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Slide ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx ? "w-10 bg-background" : "w-5 bg-background/40"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="animate-fade-up md:justify-self-end md:max-w-md md:w-full">
            <DonateWidget />
          </div>
        </div>
      </div>
    </section>
  );
}
